import { useState } from 'react';
import { Library, Pencil, Plus, Search, Trash2, X } from 'lucide-react';
import {
  LIFE_AREA_SUGGESTIONS,
  filterIntegrations,
  formatIntegratedDate,
  groupIntegrationsByBook,
  localDateKey
} from './lib/integrations';

const emptyDraft = () => ({
  id: '',
  book: '',
  principle: '',
  lifeArea: '',
  notes: '',
  implementedOn: localDateKey()
});

const fieldClass = 'w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3 py-2.5 text-sm text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500';

// Records ideas from books that are already in use. The parent owns the map
// and the Firebase write. This tab only edits a draft and renders every match.
export default function IntegratedTab({ entries = [], onSave, onDelete, syncLine = null }) {
  const [draft, setDraft] = useState(emptyDraft);
  const [query, setQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmId, setConfirmId] = useState(null);

  const editing = !!draft.id;
  const canSave = draft.book.trim() && draft.principle.trim() && draft.lifeArea.trim() && !saving;
  const filtered = filterIntegrations(entries, query);
  const groups = groupIntegrationsByBook(filtered);
  const bookCount = new Set(entries.map((entry) => entry.book.trim().toLowerCase()).filter(Boolean)).size;

  const setField = (key) => (event) => {
    setDraft((prev) => ({ ...prev, [key]: event.target.value }));
  };

  const resetDraft = () => {
    setDraft(emptyDraft());
    setConfirmId(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSave) return;
    setSaving(true);
    try {
      const ok = await onSave({ ...draft });
      if (ok) resetDraft();
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (entry) => {
    setDraft({
      id: entry.id,
      book: entry.book,
      principle: entry.principle,
      lifeArea: entry.lifeArea,
      notes: entry.notes,
      implementedOn: entry.implementedOn || localDateKey()
    });
    setConfirmId(null);
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600">
          <Library className="h-5 w-5" />
        </div>
        <h2 className="text-lg font-bold text-zinc-800">Integrated</h2>
        <p className="mx-auto mt-1 max-w-md text-sm text-zinc-500">
          Record what you have already put into practice from books you have read.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm sm:p-5">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-bold text-zinc-800">
            {editing ? 'Edit this integration' : 'Add something you already live by'}
          </h3>
          {editing && (
            <button type="button" onClick={resetDraft} className="inline-flex items-center gap-1 text-xs font-semibold text-zinc-500 hover:text-zinc-800">
              <X className="h-3.5 w-3.5" /> Cancel
            </button>
          )}
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block sm:col-span-2">
            <span className="mb-1.5 block text-xs font-bold text-zinc-700">Book</span>
            <input
              value={draft.book}
              onChange={setField('book')}
              placeholder="The 4-Hour Workweek"
              className={fieldClass}
              maxLength={200}
            />
          </label>
          <label className="block sm:col-span-2">
            <span className="mb-1.5 block text-xs font-bold text-zinc-700">What I integrated</span>
            <input
              value={draft.principle}
              onChange={setField('principle')}
              placeholder="80/20 rule"
              className={fieldClass}
              maxLength={300}
            />
          </label>
          <label className="block sm:col-span-2">
            <span className="mb-1.5 block text-xs font-bold text-zinc-700">Where it lives in my life</span>
            <input
              value={draft.lifeArea}
              onChange={setField('lifeArea')}
              placeholder="Daily productivity and time management"
              className={fieldClass}
              maxLength={200}
            />
            <span className="mt-2 flex flex-wrap gap-1.5">
              {LIFE_AREA_SUGGESTIONS.map((area) => (
                <button
                  key={area}
                  type="button"
                  onClick={() => setDraft((prev) => ({ ...prev, lifeArea: area }))}
                  className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                    draft.lifeArea === area
                      ? 'border-indigo-200 bg-indigo-50 text-indigo-700'
                      : 'border-zinc-200 bg-zinc-50 text-zinc-500 hover:bg-white hover:text-zinc-700'
                  }`}
                >
                  {area}
                </button>
              ))}
            </span>
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-bold text-zinc-700">Integrated on</span>
            <input
              type="date"
              value={draft.implementedOn}
              onChange={setField('implementedOn')}
              className={fieldClass}
            />
          </label>
          <label className="block sm:col-span-2">
            <span className="mb-1.5 block text-xs font-bold text-zinc-700">How I use it <span className="font-medium text-zinc-400">(optional)</span></span>
            <textarea
              value={draft.notes}
              onChange={setField('notes')}
              placeholder="Each morning I pick the few tasks that move the day and leave the rest."
              rows={3}
              maxLength={2000}
              className={`${fieldClass} resize-none`}
            />
          </label>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2">
          {syncLine}
          <button
            type="submit"
            disabled={!canSave}
            className="inline-flex items-center gap-2 rounded-xl bg-indigo-500 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-indigo-600 disabled:opacity-50"
          >
            <Plus className="h-4 w-4" />
            {saving ? 'Saving…' : editing ? 'Save changes' : 'Add to Integrated'}
          </button>
        </div>
      </form>

      <div className="space-y-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs font-semibold text-zinc-500">
            {entries.length === 0
              ? 'Nothing integrated yet'
              : `${entries.length} ${entries.length === 1 ? 'idea' : 'ideas'} from ${bookCount} ${bookCount === 1 ? 'book' : 'books'}`}
            {entries.length > 0 && query.trim() ? ` · ${filtered.length} matching` : ''}
            {entries.length > 0 ? ' · showing all' : ''}
          </p>
          <div className="relative w-full sm:w-64">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search book, idea, or area"
              className="w-full rounded-xl border border-zinc-200 bg-zinc-50 py-2 pl-8 pr-3 text-sm text-zinc-800 placeholder-zinc-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
            />
          </div>
        </div>

        {entries.length === 0 && (
          <div className="rounded-2xl border border-dashed border-zinc-200 bg-zinc-50 px-4 py-8 text-center">
            <p className="text-sm font-semibold text-zinc-700">Your first integration</p>
            <p className="mx-auto mt-1 max-w-sm text-sm text-zinc-500">
              The 4-Hour Workweek — 80/20 rule, used in daily life for productivity and time management.
            </p>
          </div>
        )}

        {entries.length > 0 && filtered.length === 0 && (
          <p className="rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-6 text-center text-sm text-zinc-500">
            Nothing matches that search.
          </p>
        )}

        {groups.map((group) => (
          <section key={group.book.toLowerCase()} className="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm">
            <header className="flex items-center justify-between gap-3 border-b border-zinc-100 bg-zinc-50 px-4 py-3">
              <h3 className="text-sm font-bold text-zinc-800">{group.book}</h3>
              <span className="text-[11px] font-semibold text-zinc-400">
                {group.entries.length} {group.entries.length === 1 ? 'idea' : 'ideas'}
              </span>
            </header>
            <ul className="divide-y divide-zinc-100">
              {group.entries.map((entry) => (
                <li key={entry.id} className="px-4 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-zinc-900">{entry.principle}</p>
                      <p className="mt-1 text-sm text-zinc-600">{entry.lifeArea}</p>
                      {entry.notes && (
                        <p className="mt-2 whitespace-pre-wrap text-sm text-zinc-500">{entry.notes}</p>
                      )}
                      {entry.implementedOn && (
                        <p className="mt-2 text-[11px] font-semibold uppercase tracking-wide text-zinc-400">
                          Integrated {formatIntegratedDate(entry.implementedOn)}
                        </p>
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <button
                        type="button"
                        onClick={() => startEdit(entry)}
                        className="rounded-lg p-2 text-zinc-400 hover:bg-zinc-100 hover:text-indigo-600"
                        aria-label={`Edit ${entry.principle}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      {confirmId === entry.id ? (
                        <button
                          type="button"
                          onClick={() => onDelete(entry.id)}
                          className="rounded-lg bg-red-50 px-2 py-1.5 text-[11px] font-bold text-red-600 hover:bg-red-100"
                        >
                          Remove
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setConfirmId(entry.id)}
                          className="rounded-lg p-2 text-zinc-400 hover:bg-red-50 hover:text-red-500"
                          aria-label={`Remove ${entry.principle}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}
